import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Collapse, Form, Row, Typography } from 'antd'

import { useAuthContext } from '../../../../context/auth'
import { FieldTargetType, IAccount, TEeoRequest } from '../../../../context/types'
import { usePageLeaveContext } from '../../../../context/pageLeave'
import { useEeoContext } from '../../../../context/eeo'
import SpinLoader from '../../../helpers/SpinLoader'
import {
	prepareRequestData,
	transformCustomFields,
	TValuesMap,
	updateFieldWithSelectAll,
} from '../../../../utils/custom-fields'
import CustomField from '../../../helpers/CustomField/CustomField'
import { useCustomFieldsContext } from '../../../../context/customFields'
import EEOInfo from '../../../helpers/EEOInfo/EEOInfo'
import { useAccountContext } from '../../../../context/account'
import { useParams } from 'react-router-dom'
import { useChannelContext } from '../../../../context/channel'
import { useBrandContext } from '../../../../context/brand'

const { Paragraph, Title } = Typography
const { Panel } = Collapse
type UrlParam = { accountId: string }

function AdditionalInfo() {
	const [form] = Form.useForm()
	const { auth, actions } = useAuthContext()
	const {
		channel: { channels },
	} = useChannelContext()
	const { account, actions: accountActions } = useAccountContext()
	const { pageLeave, actions: pageLeaveActions } = usePageLeaveContext()
	const { brand } = useBrandContext()
	const { eeo } = useEeoContext()
	const {
		customFields,
		customFields: { allFields, loading: customFieldsLoading },
		actions: customFieldsActions,
	} = useCustomFieldsContext()

	const [userData, setUserData] = useState<IAccount>()
	const [formErrors, setFormErrors] = useState([])
	const [allValues, setAllValues] = useState<{ [key: string]: any }>({})

	const { accountId } = useParams() as UrlParam

	useEffect(() => {
		if (account.token) {
			setUserData(account)
		} else if (!account.loading && !account.token) {
			setUserData(auth)
		}
	}, [account, auth])

	useEffect(() => {
		!accountId ? actions.setError() : accountActions.setError()
		if (userData?.user && !allFields.loaded) {
			customFieldsActions.getCustomFields(account.token)
		}

		return () => {
			pageLeaveActions.setConditions({})
		}
	}, [userData?.user])

	const initialValues = useMemo<{ [key: string]: any }>(
		() => ({
			eeo_is_disabled: userData?.user?.eeo_is_disabled,
			eeo_is_lgbtq: userData?.user?.eeo_is_lgbtq,
			eeo_is_veteran: userData?.user?.eeo_is_veteran,
			eeo_ethnicity_id: userData?.user?.ethnicity?.id,
			eeo_gender_id: userData?.user?.gender?.id,
			eeo_age_range_id: userData?.user?.ageRange?.id,
			...transformCustomFields(allFields.fields),
		}),
		[userData?.user, allFields]
	)

	const isFormDirty = useMemo(() => {
		const values = form.getFieldsValue()
		return Object.keys(values).reduce((isDirty: boolean, key: string) => {
			return isDirty || JSON.stringify(values[key]) !== JSON.stringify(initialValues[key])
		}, false)
	}, [userData?.user, form.getFieldsValue()])

	useEffect(() => {
		pageLeaveActions.setConditions({
			...pageLeave.conditions,
			additionalInfo: isFormDirty,
		})
	}, [isFormDirty])

	useEffect(() => {
		form.setFieldsValue({
			...transformCustomFields(allFields.fields),
		})
	}, [allFields])

	const reset = useCallback(() => (!accountId ? actions.setError() : accountActions.setError()), [
		actions,
		accountActions,
	])

	const onValuesChange = (values: TValuesMap, updatedValues: TValuesMap) => {
		reset()

		let newAllValues = { ...updatedValues }

		if (Object.values(values).length) {
			const field = customFields[FieldTargetType.onboarding].fields.find(({ name }) => name === Object.keys(values)[0])

			if (field?.context?.select_all) {
				newAllValues = updateFieldWithSelectAll({
					newAllValues,
					values,
					updatedValues,
					allValues,
					form,
					field,
				})
			}
		}
		setAllValues(newAllValues)
	}

	const onFinish = async (values: any) => {
		const { regular, custom } = prepareRequestData<Partial<TEeoRequest>>(allFields.fields, values)
		if (!accountId) {
			actions.updateAdditionalInfo({ regular, custom })
		} else {
			accountActions.updateAdditionalInfo({ regular, custom })
		}
	}

	const onFieldsChange = () => {
		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	const hasCustomFieldSectionTitle = !!(
		customFields.ask_experts.fields.length && customFields.ask_members.fields.length
	)

	const channelCustomFields = useMemo(() => {
		if (brand.currentBrand?.default_channel_id && customFields.channels) {
			return customFields.channels.map(({ name, id }) => {
				const channelFields = {
					[FieldTargetType.members]: customFields[FieldTargetType.members].fields.filter(({ channelId }) =>
						id === brand.currentBrand.default_channel_id ? !channelId : channelId === id
					),
					[FieldTargetType.experts]: customFields[FieldTargetType.experts].fields.filter(({ channelId }) =>
						id === brand.currentBrand.default_channel_id ? !channelId : channelId === id
					),
				}
				const hasChannelCustomFieldSectionTitle = !!(
					channelFields[FieldTargetType.experts].length && channelFields[FieldTargetType.members].length
				)

				return {
					name,
					id,
					channelFields,
					hasChannelCustomFieldSectionTitle,
				}
			})
		}

		return []
	}, [customFields.channels, brand.currentBrand, customFields])

	const showEEO = useMemo(
		() =>
			(accountId ? account.channels : channels)?.reduce(
				(show, channel) => !channel.integration_config.no_eeo || show,
				false
			),
		[channels, account.channels]
	)

	return (
		<div className="profile-form_wrapper">
			<Title level={3} style={{ marginBottom: 30 }}>
				Additional Info
			</Title>
			<SpinLoader loading={!!userData?.loading || eeo.loading || customFieldsLoading}>
				{userData?.user && (
					<div>
						<Form
							name="AdditionalInfoForm"
							form={form}
							onFinish={onFinish}
							scrollToFirstError
							layout="vertical"
							onValuesChange={onValuesChange}
							onFieldsChange={onFieldsChange}
							initialValues={initialValues}
						>
							{channelCustomFields.length > 1 ? (
								<Collapse defaultActiveKey={customFields.channels.map(({ id }) => id)} style={{ marginBottom: 40 }}>
									{channelCustomFields.map(({ name, id, hasChannelCustomFieldSectionTitle, channelFields }) => {
										return (
											<Panel header={name} key={id}>
												{hasChannelCustomFieldSectionTitle && <Title level={4}>When Providing Support</Title>}
												<Row gutter={[50, 0]}>
													{channelFields.ask_experts.map((field) => (
														<CustomField
															field={field}
															disabled={!!userData?.loading}
															form={form}
															desktopSpan={12}
															key={field.name}
														/>
													))}
												</Row>

												{hasChannelCustomFieldSectionTitle && <Title level={4}>When Looking For Support</Title>}
												<Row gutter={[50, 0]}>
													{channelFields.ask_members.map((field) => (
														<CustomField
															field={field}
															disabled={!!userData?.loading}
															form={form}
															desktopSpan={12}
															key={field.name}
														/>
													))}
												</Row>
											</Panel>
										)
									})}
								</Collapse>
							) : (
								<>
									{hasCustomFieldSectionTitle && <Title level={4}>When Providing Support</Title>}
									<Row gutter={[50, 0]}>
										{customFields.ask_experts.fields.map((field) => (
											<CustomField
												field={field}
												disabled={!!userData?.loading}
												form={form}
												desktopSpan={12}
												key={field.name}
											/>
										))}
									</Row>

									{hasCustomFieldSectionTitle && <Title level={4}>When Looking For Support</Title>}
									<Row gutter={[50, 0]}>
										{customFields.ask_members.fields.map((field) => (
											<CustomField
												field={field}
												disabled={!!userData?.loading}
												form={form}
												desktopSpan={12}
												key={field.name}
											/>
										))}
									</Row>
								</>
							)}

							{showEEO && (
								<>
									<Title level={4}>More About Me</Title>
									<EEOInfo form={form} />
								</>
							)}

							<Form.Item>
								<div className="btn-actions">
									<Button shape="round" type="primary" htmlType="submit" loading={!!userData?.loading}>
										Update
									</Button>
								</div>
							</Form.Item>
							{userData?.error && <Alert showIcon message="Error" description={userData?.error} type="error" />}
							{formErrors.length > 0 && (
								<Alert
									showIcon
									type="warning"
									message="Warning"
									description={formErrors.map((v) => (
										<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
											{v.errors[0]}
										</Paragraph>
									))}
								/>
							)}
						</Form>
					</div>
				)}
			</SpinLoader>
		</div>
	)
}

export default AdditionalInfo
